"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.applyActions = exports.checkTrigger = exports.hasLogic = void 0;
exports.setActionBooleanProperty = setActionBooleanProperty;
exports.setActionStringProperty = setActionStringProperty;
exports.setActionProperty = setActionProperty;
exports.setValueProperty = setValueProperty;
exports.setMergeComponentSchema = setMergeComponentSchema;
exports.setCustomAction = setCustomAction;
const conditions_1 = require("./conditions");
const lodash_1 = require("lodash");
const utils_1 = require("./utils");
const formUtil_1 = require("./formUtil");
const hasLogic = (context) => {
    const { component } = context;
    const { logic } = component;
    if (!logic || !logic.length) {
        return false;
    }
    return true;
};
exports.hasLogic = hasLogic;
const checkTrigger = (context, trigger) => {
    let shouldTrigger = false;
    if (!trigger) {
        return false;
    }
    switch (trigger.type) {
        case 'simple':
            if ((0, conditions_1.isLegacyConditional)(trigger.simple)) {
                shouldTrigger = (0, conditions_1.checkLegacyConditional)(trigger.simple, context);
            }
            else {
                shouldTrigger = (0, conditions_1.checkSimpleConditional)(trigger.simple, context);
            }
            break;
        case 'javascript':
            shouldTrigger = (0, conditions_1.checkCustomConditional)(trigger.javascript, context, 'result');
            break;
        case 'json':
            shouldTrigger = (0, conditions_1.checkJsonConditional)(trigger, context);
            break;
        default:
            shouldTrigger = false;
            break;
    }
    if (shouldTrigger === null) {
        return false;
    }
    return shouldTrigger;
};
exports.checkTrigger = checkTrigger;
function setActionBooleanProperty(context, action) {
    var _a, _b;
    const { component, scope, path } = context;
    const property = action.property.value;
    const currentValue = (0, lodash_1.get)(component, property, false).toString();
    const newValue = action.state.toString();
    if (currentValue !== newValue) {
        (0, lodash_1.set)(component, property, newValue === 'true');
        // If this is "logic" forcing a component to set hidden property, then we will set the "conditionallyHidden"
        // flag which will trigger the clearOnHide functionality.
        if (property === 'hidden' && path) {
            if (!scope.conditionals) {
                scope.conditionals = [];
            }
            const conditionallyHidden = (_a = scope.conditionals) === null || _a === void 0 ? void 0 : _a.find((cond) => {
                return cond.path === path;
            });
            if (conditionallyHidden) {
                conditionallyHidden.conditionallyHidden = !!component.hidden;
                (0, formUtil_1.setComponentScope)(component, 'conditionallyHidden', !!component.hidden);
            }
            else {
                (_b = scope.conditionals) === null || _b === void 0 ? void 0 : _b.push({
                    path,
                    conditionallyHidden: !!component.hidden,
                });
            }
        }
        return true;
    }
    return false;
}
function setActionStringProperty(context, action) {
    const { component, data, row, result } = context;
    const property = action.property.value;
    const textValue = action.property.component
        ? action[action.property.component]
        : action.text;
    const currentValue = (0, lodash_1.get)(component, property, '');
    const newValue = (0, utils_1.interpolate)(textValue, Object.assign(Object.assign({}, context), { value: '', data: (0, utils_1.escapeInterpolationDataStrings)(data), row: (0, utils_1.escapeInterpolationDataStrings)(row), result: typeof result === 'string' ? (0, lodash_1.escape)(result) : (0, utils_1.escapeInterpolationDataStrings)(result) }), (evalContext) => {
        evalContext.value = currentValue;
    });
    if (newValue !== currentValue) {
        (0, lodash_1.set)(component, property, newValue);
        return true;
    }
    return false;
}
function setActionProperty(context, action) {
    switch (action.property.type) {
        case 'boolean':
            return setActionBooleanProperty(context, action);
        case 'string':
            return setActionStringProperty(context, action);
    }
    return false;
}
function setValueProperty(context, action) {
    var _a;
    const { component, data, path, scope } = context;
    const oldValue = (0, lodash_1.get)(data, path);
    const newValue = (0, utils_1.evaluate)(action.value, context, 'value', false, (evalContext) => {
        evalContext.value = (0, lodash_1.clone)(oldValue);
        evalContext.result = context.result;
    });
    if (!(0, lodash_1.isEqual)(oldValue, newValue) &&
        !(component.clearOnHide && (0, conditions_1.conditionallyHidden)(context))) {
        (0, lodash_1.set)(data, path, newValue);
        if (!scope.filter)
            scope.filter = {};
        if (!((_a = scope.clearHidden) === null || _a === void 0 ? void 0 : _a.hasOwnProperty(path))) {
            scope.filter[path] = true;
        }
        return true;
    }
    return false;
}
function setMergeComponentSchema(context, action) {
    const { component, data, path, row, result } = context;
    const oldValue = (0, lodash_1.get)(data, path);
    const safeData = (0, utils_1.escapeInterpolationDataStrings)(data);
    const safeRow = (0, utils_1.escapeInterpolationDataStrings)(row);
    const safeResult = typeof result === 'string' ? (0, lodash_1.escape)(result) : (0, utils_1.escapeInterpolationDataStrings)(result);
    const schema = (0, utils_1.evaluate)(action.schemaDefinition, Object.assign(Object.assign({}, context), { value: {}, data: safeData, row: safeRow, result: safeResult }), 'schema', false, (evalContext) => {
        evalContext.value = (0, lodash_1.clone)(oldValue);
        evalContext.result = safeResult;
    });
    const merged = (0, lodash_1.assign)({}, component, schema);
    if (!(0, lodash_1.isEqual)(component, merged)) {
        (0, lodash_1.assign)(component, schema);
        return true;
    }
    return false;
}
function setCustomAction(context, action) {
    return setValueProperty(context, { type: 'value', value: action.customAction });
}
const applyActions = (context) => {
    const { component } = context;
    const { logic } = component;
    if (!logic || !logic.length) {
        return false;
    }
    return logic.reduce((changed, logicItem) => {
        const { actions, trigger } = logicItem;
        const result = (0, exports.checkTrigger)(context, trigger);
        if (!trigger || !actions || !actions.length || !result) {
            return changed;
        }
        context.result = result;
        const actionsResult = actions.reduce((changed, action) => {
            switch (action.type) {
                case 'property':
                    if (setActionProperty(context, action)) {
                        return true;
                    }
                    return changed;
                case 'value':
                    return setValueProperty(context, action) || changed;
                case 'mergeComponentSchema':
                    if (setMergeComponentSchema(context, action)) {
                        return true;
                    }
                    return changed;
                case 'customAction':
                    return setCustomAction(context, action) || changed;
                default:
                    return changed;
            }
        }, changed);
        // remove result of current logic block from context
        (0, lodash_1.unset)(context, 'result');
        return actionsResult;
    }, false);
};
exports.applyActions = applyActions;
